<script setup>
import { computed, onMounted, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentAssignmentList from './AgentAssignmentList.vue';
import {
  buildAgentAssignmentSections,
  fetchAgentTeamData,
  UNASSIGNED_VALUE,
} from 'dashboard/helper/agentAssignmentMenu';

const props = defineProps({
  modelValue: {
    type: Number,
    default: null,
  },
  aiAgentModelValue: {
    type: Number,
    default: null,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  aiAgents: {
    type: Array,
    default: () => [],
  },
  fallbackAgent: {
    type: Object,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    default: '',
  },
  buttonClass: {
    type: [String, Array, Object],
    default: '',
  },
  nameClass: {
    type: [String, Array, Object],
    default: '',
  },
  dropdownClass: {
    type: [String, Array, Object],
    default: '',
  },
  avatarSize: {
    type: Number,
    default: 24,
  },
  thumbnailSize: {
    type: Number,
    default: 24,
  },
  nameMaxWidthClass: {
    type: String,
    default: 'max-w-[92px]',
  },
  agentsTitle: {
    type: String,
    default: '',
  },
  aiAgentsTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:aiAgentModelValue',
  'open',
  'close',
  'select',
  'selectAiAgent',
]);

const { t } = useI18n();
const store = useStore();
const isOpen = ref(false);
const teams = computed(() => store.getters['teams/getTeams']);

const selectedAiAgent = computed(() => {
  if (!props.aiAgentModelValue) return null;

  return (
    props.aiAgents.find(agent => agent.id === props.aiAgentModelValue) || null
  );
});

const selectedAgent = computed(() => {
  if (selectedAiAgent.value) {
    return selectedAiAgent.value;
  }

  if (props.modelValue) {
    return props.agents.find(agent => agent.id === props.modelValue) || null;
  }

  return props.fallbackAgent || null;
});

const selectedAgentName = computed(
  () => selectedAgent.value?.available_name || selectedAgent.value?.name || ''
);

const selectedAiAgentEmoji = computed(
  () => selectedAiAgent.value?.emoji || '🤖'
);

const selectedAgentAvatar = computed(
  () => selectedAgent.value?.thumbnail || selectedAgent.value?.avatar_url || ''
);

const selectedAgentAvailability = computed(
  () => selectedAgent.value?.availability_status || ''
);

const menuSections = computed(() => {
  return buildAgentAssignmentSections({
    humanAgents: props.agents,
    aiAgents: props.aiAgents,
    teams: teams.value,
    getTeamMembers: teamId =>
      store.getters['teamMembers/getTeamMembers'](teamId) || [],
    selectedUserId: props.modelValue,
    selectedAiAgentId: props.aiAgentModelValue,
    includeUnassigned: true,
    unassignedLabel: t('CONVERSATION.UNASSIGNED'),
    unassignedAction: 'clearSelection',
    userAction: 'selectAgent',
    humanAgentsTitle:
      props.agentsTitle || t('AGENT_MGMT.MULTI_SELECTOR.AGENTS'),
    aiAction: 'selectAiAgent',
    aiAgentsTitle: props.aiAgentsTitle || t('CHAT_LIST.OMNI_SIDEBAR.AI_AGENTS'),
    aiAgentsBadgeLabel: t('GENERAL.BETA'),
  });
});

onMounted(() => {
  fetchAgentTeamData(store);
});

function openDropdown() {
  isOpen.value = true;
  emit('open');
}

function closeDropdown() {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close');
}

function toggleDropdown() {
  if (isOpen.value) {
    closeDropdown();
    return;
  }

  openDropdown();
}

function handleSelect(payload) {
  emit('select', payload);

  if (
    payload.action === 'clearSelection' ||
    payload.value === UNASSIGNED_VALUE
  ) {
    emit('update:modelValue', null);
    emit('update:aiAgentModelValue', null);
    closeDropdown();
    return;
  }

  if (payload.action === 'selectAiAgent') {
    emit('update:modelValue', null);
    emit('update:aiAgentModelValue', payload.value);
    emit('selectAiAgent', payload);
    closeDropdown();
    return;
  }

  emit('update:aiAgentModelValue', null);
  emit('update:modelValue', payload.value);
  closeDropdown();
}
</script>

<template>
  <OnClickOutside @trigger="closeDropdown">
    <div class="relative">
      <button type="button" :class="buttonClass" @click="toggleDropdown">
        <span
          v-if="selectedAiAgent"
          class="flex items-center justify-center rounded-full bg-n-alpha-2 text-[12px]"
          :style="{ width: `${avatarSize}px`, height: `${avatarSize}px` }"
        >
          {{ selectedAiAgentEmoji }}
        </span>
        <Avatar
          v-else
          :name="selectedAgentName || placeholder"
          :src="selectedAgentAvatar"
          :status="selectedAgentAvailability"
          :size="avatarSize"
          rounded-full
        />
        <span :class="[nameMaxWidthClass, nameClass]">
          {{ selectedAgentName || placeholder }}
        </span>
        <span
          class="i-lucide-chevron-down size-3 text-n-slate-10 transition-transform duration-150"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>

      <transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="translate-y-1 scale-95 opacity-0"
        enter-to-class="translate-y-0 scale-100 opacity-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="translate-y-0 scale-100 opacity-100"
        leave-to-class="translate-y-1 scale-95 opacity-0"
      >
        <div
          v-if="isOpen"
          class="absolute z-50 rounded-xl border-0 bg-n-alpha-3 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px]"
          :class="dropdownClass"
        >
          <AgentAssignmentList
            :sections="menuSections"
            :thumbnail-size="thumbnailSize"
            :search-placeholder="
              t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
            "
            @select="handleSelect"
          />
        </div>
      </transition>
    </div>
  </OnClickOutside>
</template>
